import { Box, Grid } from "@mui/material";
import StatsCardComponent from "components/cards/stats/current/StatsCard.component";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  propsCo2Card,
  propsPM10Card,
  propsPM2_5Card,
  propsTempertureCard,
} from "constants/metrics";
import Request from "core/requests";
import { useTranslation } from "react-i18next";
import { green, red } from "@mui/material/colors";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Loading } from "components/common";
import TitleSectionComponent from "components/cards/title/TitleSection.component";
import { saveMetric } from "store/user.store";
import { optionsMetrics } from "constants/metrics";
import { useNavigate } from "react-router-dom";

const StatsContainer = ({ zoneSelected, onError = () => { }, refresh, stationIdSelected }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token, loading: loadingRefresh } = useSelector((state) => state.user);
  const [loading, setLoading] = useState(true);
  const [dataStats, setDataStats] = useState(null);
  const [data, setData] = useState(null);

  const propsCard = useMemo(
    () => ({
      air_temp: {
        title: t("Heat Wave"),
        primaryColor: propsTempertureCard.avatarcolor,
      },
      CO2: {
        title: t("CO2 level"),
        primaryColor: propsCo2Card.avatarcolor,
      },
      pm10: {
        title: t("PM 10 Level"),
        primaryColor: propsPM10Card.avatarcolor,
      },
      pm2_5: {
        title: t("PM 2.5 Level"),
        primaryColor: propsPM2_5Card.avatarcolor,
      },

    }),
    [t]
  );

  const onClickCard = useCallback((item) => {
    if (item === "wind_speed" || item === "wind_dir_idx") {
      dispatch(
        saveMetric(
          optionsMetrics.filter((e) => e.value === "wind_speed,wind_dir_idx")[0]
        )
      );
    } else {
      if (item === "rain_intensity" || item === "rain_intensity_peak") {
        dispatch(
          saveMetric(
            optionsMetrics.filter(
              (e) => e.value === "rain_intensity,rain_intensity_peak"
            )[0]
          )
        );
      } else if (item === "pm10" || item === "pm2_5") {
        dispatch(
          saveMetric(optionsMetrics.filter((e) => e.value === "pm10,pm2_5")[0])
        );
      } else {
        dispatch(saveMetric(optionsMetrics.filter((e) => e.value === item)[0]));
      }
    }
    navigate("/dashboard/data-sensors/metrics");
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (data) {
      let obj = {};
      Object.keys(data).map((key) => {
        if (key !== "time") {
          switch (key) {
            case "air_temp":
              obj = {
                ...obj,
                [key]: {
                  labelBottom: t("Maximum day"),
                  contentBottom:
                    data[key]?.data !== null ? `${data[key]?.data} °C` : null,

                  labelCenter: `${data[key]?.hot_wave_status !== null
                    ? data[key]?.hot_wave_status === 0
                      ? `${t("Not in progress")}`
                      : `${t("In progress")} ${t("from")} ${data[key]?.hot_wave_days
                      } 
                      ${t("Days").toLocaleLowerCase()}`
                    : t("Data not available")
                    }`,
                  valueCenter:
                    data[key]?.hot_wave_status !== null &&
                      data[key]?.hot_wave_status !== 0
                      ? `${`${data[key]?.hot_wave_status === 2
                        ? `(${t("long wave")})`
                        : `(${t("short wave")})`
                      }`}`
                      : undefined,
                  type:
                    data[key]?.hot_wave_status !== null
                      ? data[key]?.hot_wave_status === 1
                        ? "warning"
                        : data[key]?.hot_wave_status === 2
                          ? "critic"
                          : "ok"
                      : null,
                  ...propsCard[key],
                },
              };
              break;

            case "CO2":
              obj = {
                ...obj,
                [key]: {
                  labelBottom: t("Average day"),
                  contentBottom:
                    data[key]?.data !== null ? `${data[key]?.data} ppm` : null,

                  labelCenter:
                    data[key]?.level !== null
                      ? `${data[key]?.level === 1
                        ? t("warning")
                        : data[key]?.level === 2
                          ? t("critic")
                          : t("Acceptable")
                      }`
                      : t("Data not available"),
                  type:
                    data[key]?.level !== null
                      ? data[key]?.level === 1
                        ? "warning"
                        : data[key]?.level === 2
                          ? "critic"
                          : "ok"
                      : null,
                  ...propsCard[key],
                },
              };
              break;

            case "pm2_5":
              obj = {
                ...obj,
                [key]: {
                  valueCenter: data[key]?.data_year
                    ? `${data[key]?.data_year} µg/m³`
                    : null,
                  labelCenter: data[key]?.data_year ? t("Annual average") : t("Data not available"),
                  labelBottom: t("Average day"),

                  contentBottom: data[key]?.data
                    ? `${data[key]?.data} µg/m³`
                    : null,

                  days: data[key]?.data,
                  type:
                    data[key]?.over_legal_limit_year !== null
                      ? data[key]?.over_legal_limit_year
                        ? "critic"
                        : "ok"
                      : null,
                  ...propsCard[key],
                },
              };
              break;

            case "pm10":
              obj = {
                ...obj,
                [key]: {
                  labelCenter: data[key]?.data_year ? t("Annual average") : t("Data not available"),
                  valueCenter: data[key]?.data_year
                    ? `${data[key]?.data_year} µg/m³`
                    : null,
                  labelBottom: t("Average day"),
                  contentBottom: data[key]?.data ? (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {data[key]?.data
                        ? `${data[key]?.data} µg/m³`
                        : t("Data not available")}

                      {data[key]?.over_legal_limit ? (
                        <ErrorOutlineIcon
                          style={{
                            color: red[500],
                            width: 13,
                            height: 13,
                            zIndex: 20,
                            marginLeft: 5,
                          }}
                        />
                      ) : (
                        <CheckCircleOutlineIcon
                          style={{
                            color: green[500],
                            width: 13,
                            height: 13,
                            zIndex: 20,
                            marginLeft: 5,
                          }}
                        />
                      )}
                    </Box>
                  ) : null,

                  type:
                    data[key]?.over_legal_limit_status !== null
                      ? data[key]?.over_legal_limit_status === 1
                        ? "warning"
                        : data[key]?.over_legal_limit_status === 2
                          ? "critic"
                          : "ok"
                      : null,
                  comulativeDays: data[key]?.over_legal_limit_days_year_count,
                  comulativeDaysStatus:
                    data[key]?.over_legal_limit_days_year !== null
                      ? data[key]?.over_legal_limit_days_year === 1
                        ? "warning"
                        : data[key]?.over_legal_limit_days_year === 2
                          ? "critic"
                          : "ok"
                      : null,
                  ...propsCard[key],
                },
              };

              break;

            default:
              return obj;
          }
        }
        return obj;
      });
      setDataStats(obj);
    }
  }, [data, t, propsCard]);

  useEffect(() => {
    if (!loadingRefresh) {
      if (stationIdSelected) {
        setLoading(true);
        Request.stations
          .getStatsStation({
            token: token,
            station_id: _.split(stationIdSelected, "-")[0].trim(),
          })
          .then((resp) => {
            setData(resp);
            setLoading(false);
          })
          .catch((error) => {
            setLoading(false);
            onError(error);
          });
      } else {
        setLoading(true);
        Request.stations
          .getStats({
            token: token,
            path: !_.isEmpty(zoneSelected) ? `?cluster=${zoneSelected}` : "",
          })
          .then((resp) => {
            setData(resp);
            setLoading(false);
          })
          .catch((error) => {
            setLoading(false);
            onError(error);
          });
      }
    }
    // eslint-disable-next-line
  }, [zoneSelected, token, refresh, stationIdSelected, loadingRefresh]);



  return loading ? (
    <Loading />
  ) : (
    <Grid container spacing={2}>
      {(!stationIdSelected && zoneSelected?.length >= 2) ? (
        <Grid item xs={12}>
          <TitleSectionComponent
            title={t("Stats not available with 2 or more zones selected")}
            sx={{ align: "center" }}
          />
        </Grid>
      ) : (
        dataStats &&
        Object.keys(propsCard).map((key, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatsCardComponent
              {...dataStats[key]}
              time={data?.time}
              onClickCard={() => onClickCard(key)}
            />
          </Grid>
        ))
      )}
    </Grid>
  );
};

export default StatsContainer;
